import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { meetingApi, employeeApi } from '../../services/api';
import { Spinner, Modal, EmptyState } from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  CheckCircleIcon,
  PlusIcon,
  CalendarIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

const TYPE_STYLES = {
  one_on_one:    { label: '1:1',         color: 'bg-blue-100 text-blue-700 border-blue-200' },
  onboarding:    { label: 'Onboarding',  color: 'bg-green-100 text-green-700 border-green-200' },
  performance:   { label: 'Performance', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  development:   { label: 'Development', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  pip_check_in:  { label: 'PIP check-in',color: 'bg-red-100 text-red-700 border-red-200' },
};

const STATUS_STYLES = {
  scheduled: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-600',
};

function AddItemForm({ employees, saving, onAdd }) {
  const [title, setTitle]           = useState('');
  const [notes, setNotes]           = useState('');
  const [isAction, setIsAction]     = useState(false);
  const [assignedTo, setAssignedTo] = useState('');
  const [dueDate, setDueDate]       = useState('');

  const submit = () => {
    if (!title.trim()) return;
    onAdd({
      title:          title.trim(),
      notes:          notes.trim() || null,
      is_action_item: isAction,
      assigned_to:    isAction ? (assignedTo || null) : null,
      due_date:       isAction && dueDate ? dueDate : null,
    });
    setTitle(''); setNotes(''); setIsAction(false); setAssignedTo(''); setDueDate('');
  };

  return (
    <div className="card p-4 space-y-3">
      <div>
        <label className="label">Title</label>
        <input className="input" placeholder="Agenda item or action…" value={title} onChange={e => setTitle(e.target.value)} />
      </div>
      <div>
        <label className="label">Notes (optional)</label>
        <textarea className="input h-20 resize-none" value={notes} onChange={e => setNotes(e.target.value)} />
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={isAction} onChange={e => setIsAction(e.target.checked)} className="w-4 h-4 rounded border-gray-300" />
        Mark as action item
      </label>
      {isAction && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label">Assigned to</label>
            <select className="input" value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
              <option value="">Select…</option>
              {(employees || []).map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Due date</label>
            <input type="date" className="input" value={dueDate} onChange={e => setDueDate(e.target.value)} />
          </div>
        </div>
      )}
      <div className="flex justify-end">
        <button disabled={!title.trim() || saving} onClick={submit} className="btn-primary disabled:opacity-40">
          <PlusIcon className="w-4 h-4" />{saving ? 'Adding…' : 'Add item'}
        </button>
      </div>
    </div>
  );
}

export default function MeetingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [completing, setCompleting] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');

  const { data: meeting, isLoading } = useQuery({
    queryKey: ['meeting', id],
    queryFn:  () => meetingApi.get(id).then(r => r.data),
  });

  const { data: employees } = useQuery({
    queryKey: ['employees', 'all'],
    queryFn:  () => employeeApi.list().then(r => r.data?.data || r.data),
  });

  const addItemMut = useMutation({
    mutationFn: (data) => meetingApi.addItem(id, data),
    onSuccess:  () => { toast.success('Item added'); qc.invalidateQueries(['meeting', id]); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to add item'),
  });

  const toggleItemMut = useMutation({
    mutationFn: ({ itemId, completed }) => meetingApi.updateItem(id, itemId, {
      completed_at: completed ? new Date().toISOString() : null,
    }),
    onSuccess: () => { qc.invalidateQueries(['meeting', id]); },
    onError:   (e) => toast.error(e.response?.data?.message || 'Failed to update'),
  });

  const completeMut = useMutation({
    mutationFn: (data) => meetingApi.complete(id, data),
    onSuccess:  () => { toast.success('Meeting completed'); qc.invalidateQueries(['meeting', id]); setCompleting(false); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to complete'),
  });

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  if (!meeting) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <EmptyState title="Meeting not found" description="It may have been deleted or you don't have access." action={<button onClick={() => navigate('/app/meetings')} className="btn-primary">Back to meetings</button>} />
      </div>
    );
  }

  const type = TYPE_STYLES[meeting.type] || TYPE_STYLES.one_on_one;
  const items = meeting.items || [];
  const agenda = items.filter(i => !i.is_action_item);
  const actions = items.filter(i => i.is_action_item);
  const empMap = new Map((employees || []).map(e => [e.id, e]));

  const renderItem = (item) => {
    const completed = !!item.completed_at;
    const assignee = item.assigned_to ? empMap.get(item.assigned_to) : null;
    return (
      <li key={item.id} className="flex items-start gap-3 p-3 border-b border-gray-100 last:border-0">
        <input
          type="checkbox"
          checked={completed}
          onChange={(e) => toggleItemMut.mutate({ itemId: item.id, completed: e.target.checked })}
          className="mt-1 w-4 h-4 rounded border-gray-300"
        />
        <div className="flex-1 min-w-0">
          <p className={clsx('text-sm font-medium', completed ? 'text-gray-400 line-through' : 'text-gray-900')}>{item.title}</p>
          {item.notes && <p className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">{item.notes}</p>}
          {item.is_action_item && (
            <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-gray-500">
              {assignee && <span>Assigned to <span className="font-medium text-gray-700">{assignee.name}</span></span>}
              {item.due_date && <span className="flex items-center gap-1"><CalendarIcon className="w-3 h-3" />{new Date(item.due_date).toLocaleDateString()}</span>}
            </div>
          )}
        </div>
      </li>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Link to="/app/meetings" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mb-4">
        <ArrowLeftIcon className="w-4 h-4" /> Back to meetings
      </Link>

      <div className="card p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border', type.color)}>{type.label}</span>
              <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize', STATUS_STYLES[meeting.status] || 'bg-gray-100 text-gray-600')}>{meeting.status}</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              {meeting.manager?.name || 'Manager'} &nbsp;·&nbsp; {meeting.employee?.name || 'Employee'}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Scheduled {meeting.scheduled_at ? new Date(meeting.scheduled_at).toLocaleString() : '—'}
            </p>
          </div>
          {meeting.status === 'scheduled' && (
            <button onClick={() => setCompleting(true)} className="btn-primary">
              <CheckCircleIcon className="w-4 h-4" /> Complete meeting
            </button>
          )}
        </div>
        {meeting.notes && (
          <div className="mt-4 bg-gray-50 border border-gray-100 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Meeting notes</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{meeting.notes}</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="section-title mb-3">Agenda</h2>
          <div className="card">
            {agenda.length === 0
              ? <p className="text-sm text-gray-400 py-6 text-center">No agenda items yet.</p>
              : <ul>{agenda.map(renderItem)}</ul>}
          </div>
        </div>
        <div>
          <h2 className="section-title mb-3">Action items</h2>
          <div className="card">
            {actions.length === 0
              ? <p className="text-sm text-gray-400 py-6 text-center">No action items yet.</p>
              : <ul>{actions.map(renderItem)}</ul>}
          </div>
        </div>
      </div>

      <h2 className="section-title mt-8 mb-3">Add item</h2>
      <AddItemForm employees={employees} saving={addItemMut.isPending} onAdd={(data) => addItemMut.mutate(data)} />

      <Modal open={completing} onClose={() => setCompleting(false)} title="Complete meeting" size="md">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Capture a brief summary and mark this meeting as completed. Action items remain open until individually checked off.</p>
          <div>
            <label className="label">Final notes</label>
            <textarea className="input h-28 resize-none" value={completionNotes} onChange={e => setCompletionNotes(e.target.value)} placeholder="Key decisions, outcomes, follow-ups…" />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setCompleting(false)} className="btn-secondary">Cancel</button>
            <button disabled={completeMut.isPending} onClick={() => completeMut.mutate({ notes: completionNotes })} className="btn-primary disabled:opacity-40">
              {completeMut.isPending ? 'Completing…' : 'Complete meeting'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
